import { Box, type SxProps, type Theme } from "@mui/material";
import React from "react";

import { LoadingRing } from "@/components/common/LoadingRing";
import { PageShell } from "@/components/common/PageShell";

interface ReportingPageHeaderShellProps {
  title: string;
  icon?: React.ReactNode;
  gradientTitle?: boolean;
  loading?: boolean;
  headerControls?: React.ReactNode;
  secondaryControls?: React.ReactNode;
  printMeta?: React.ReactNode;
  contentSx?: SxProps<Theme>;
}

interface InsightsHeaderSelectorRowProps {
  children: React.ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
}

interface InsightsHeaderSelectorFieldProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

const defaultContentSx: SxProps<Theme> = {
  pt: 0,
  pb: 0,
};

/**
 * Shared responsive layout for insight and report context selectors.
 * Selector fields are trailing inline from `md` up, and stack below it.
 */
export const InsightsHeaderSelectorRow: React.FC<InsightsHeaderSelectorRowProps> = ({
  children,
  className,
  sx,
}) => (
  <Box
    className={className}
    sx={{
      display: "flex",
      width: "100%",
      alignItems: "center",
      gap: 1.5,
      flexWrap: { xs: "wrap", md: "nowrap" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const InsightsHeaderSelectorField: React.FC<InsightsHeaderSelectorFieldProps> = ({
  children,
  sx,
}) => (
  <Box
    sx={{
      width: { xs: "100%", md: undefined },
      flex: { xs: "1 1 100%", md: "0 1 auto" },
      minWidth: 0,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const ReportingPageHeaderShell: React.FC<ReportingPageHeaderShellProps> = ({
  title,
  icon,
  gradientTitle,
  loading = false,
  headerControls,
  secondaryControls,
  printMeta,
  contentSx,
}) => {
  return (
    <PageShell
      title={title}
      gradientTitle={gradientTitle}
      icon={icon}
      titleAccessory={<LoadingRing loading={loading} />}
      headerActions={headerControls}
      contextControls={
        secondaryControls || printMeta ? (
          <Box sx={contentSx ?? defaultContentSx}>
            {secondaryControls}
            {printMeta}
          </Box>
        ) : null
      }
    >
      {null}
    </PageShell>
  );
};

type ReportingPageHeaderVariantProps = Omit<ReportingPageHeaderShellProps, "contentSx">;

export const ComparisonPageHeaderShell: React.FC<ReportingPageHeaderVariantProps> = (props) => (
  <ReportingPageHeaderShell {...props} />
);

export const ReportPageHeaderShell: React.FC<ReportingPageHeaderVariantProps> = (props) => (
  <ReportingPageHeaderShell
    {...props}
    contentSx={{
      pt: 0,
      pb: 0,
    }}
  />
);
